import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, X, Clock, MapPin, Calendar, BookOpen, ChevronDown, ChevronUp, GraduationCap, Building2, ArrowLeft } from 'lucide-react';
import api from '../components/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

const JOURS_SEMAINE = [
  { id: 1, libelle: 'Lundi', color: 'bg-blue-50 border-blue-200', icon: '🌅' },
  { id: 2, libelle: 'Mardi', color: 'bg-purple-50 border-purple-200', icon: '🌤️' },
  { id: 3, libelle: 'Mercredi', color: 'bg-green-50 border-green-200', icon: '☀️' },
  { id: 4, libelle: 'Jeudi', color: 'bg-yellow-50 border-yellow-200', icon: '🌞' },
  { id: 5, libelle: 'Vendredi', color: 'bg-red-50 border-red-200', icon: '🌅' },
  { id: 6, libelle: 'Samedi', color: 'bg-indigo-50 border-indigo-200', icon: '🌤️' },
];

const EditEmplois = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [salles, setSalles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedSemestre, setSelectedSemestre] = useState('');
  const [cours, setCours] = useState([]);
  const [expandedDay, setExpandedDay] = useState(1);
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          emploiResponse,
          classesResponse,
          semestresResponse,
          sallesResponse
        ] = await Promise.all([
          api.get(`/emplois/${id}/`),
          api.get('/classe/get-all-classe/'),
          api.get('/emplois/semestres/actifs/'),
          api.get('/etablissement/salles/')
        ]);

        const emploiData = emploiResponse.data;
        setSelectedClasse(emploiData.classe?.id?.toString() || '');
        setSelectedSemestre(emploiData.semestre?.id?.toString() || '');
        setCours(emploiData.cours.map(c => ({
          id: Date.now() + Math.random(),
          jour: c.jour,
          heure_debut: c.heure_debut,
          heure_fin: c.heure_fin,
          module: c.module?.id?.toString() || '',
          salle: c.salle?.id?.toString() || ''
        })));
        setClasses(classesResponse.data || []);
        setSemestres(semestresResponse.data || []);
        setSalles(sallesResponse.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast.error('Erreur lors du chargement des données');
        navigate('/liste-emplois');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  useEffect(() => {
    const fetchModules = async () => {
      if (selectedClasse) {
        setLoadingModules(true);
        try {
          const response = await api.get(`/emplois/modules-by-classe/${selectedClasse}/`);
          setModules(response.data || []);
        } catch (error) {
          console.error('Erreur lors du chargement des modules:', error);
          toast.error('Erreur lors du chargement des modules');
        } finally {
          setLoadingModules(false);
        }
      } else {
        setModules([]);
      }
    };

    fetchModules();
  }, [selectedClasse]);

  const handleAddCours = (jourId) => {
    const newCours = {
      id: Date.now(),
      jour: jourId,
      heure_debut: '',
      heure_fin: '',
      module: '',
      salle: '',
    };
    setCours([...cours, newCours]);
  };

  const getCoursForDay = (jourId) => {
    return cours.filter(c => c.jour === jourId);
  };

  const handleRemoveCours = (id) => {
    setCours(cours.filter(c => c.id !== id));
  };

  const handleCoursChange = (id, field, value) => {
    setCours(cours.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClasse || !selectedSemestre) {
      toast.error('Veuillez sélectionner une classe et un semestre');
      return;
    }

    if (cours.length === 0) {
      toast.error('Veuillez ajouter au moins un cours');
      return;
    }

    const coursInvalides = cours.filter(c => 
      !c.module || !c.salle || !c.heure_debut || !c.heure_fin
    );
    
    if (coursInvalides.length > 0) {
      toast.error('Certains cours ont des informations manquantes');
      return;
    }

    setSaving(true);
    try {
      const anneeResponse = await api.get('/etablissement/annees-academiques/active/');
      
      if (!anneeResponse.data || !anneeResponse.data.id) {
        toast.error('Impossible de récupérer l\'année académique active');
        setSaving(false);
        return;
      }

      const classeId = parseInt(selectedClasse);
      const semestreId = parseInt(selectedSemestre);
      const anneeId = parseInt(anneeResponse.data.id);
      
      if (isNaN(classeId) || isNaN(semestreId) || isNaN(anneeId)) {
        toast.error('Données invalides');
        setSaving(false);
        return;
      }

      const emploiData = {
        classe_id: classeId,
        semestre_id: semestreId,
        annee_id: anneeId,
        cours: cours.map(({ id, ...rest }) => {
          const moduleId = parseInt(rest.module);
          const salleId = parseInt(rest.salle);
          const jour = parseInt(rest.jour);
          
          if (isNaN(moduleId) || isNaN(salleId) || isNaN(jour)) {
            throw new Error('Données de cours invalides');
          }
          
          let heure_debut = rest.heure_debut;
          let heure_fin = rest.heure_fin;
          
          if (heure_debut && !heure_debut.includes(':')) {
            heure_debut = `${heure_debut}:00`;
          } else if (heure_debut && heure_debut.split(':').length === 2) {
            heure_debut = `${heure_debut}:00`;
          }
          
          if (heure_fin && !heure_fin.includes(':')) {
            heure_fin = `${heure_fin}:00`;
          } else if (heure_fin && heure_fin.split(':').length === 2) {
            heure_fin = `${heure_fin}:00`;
          }
          
          return {
            jour,
            module: moduleId,
            salle: salleId,
            heure_debut,
            heure_fin
          };
        })
      };

      await api.put(`/emplois/${id}/`, emploiData);
      toast.success('Emploi du temps modifié avec succès');
      navigate('/liste-emplois');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      let errorMessage = 'Erreur lors de la modification de l\'emploi du temps';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const messages = Object.entries(error.response.data)
            .map(([field, message]) => `${field}: ${Array.isArray(message) ? message[0] : message}`);
          errorMessage = messages.join('\n');
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#202654] to-[#2a2f6c]">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/liste-emplois')}
                className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">Modifier l'Emploi du Temps</h1>
                <p className="mt-1 text-sm text-gray-200">
                  Modifiez l'emploi du temps existant
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <GraduationCap className="h-6 w-6 text-[#202654]" />
                  </div>
                  <label htmlFor="classe" className="block text-sm font-medium text-gray-700">
                    Classe
                  </label>
                </div>
                <select
                  id="classe"
                  value={selectedClasse}
                  onChange={(e) => setSelectedClasse(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                  required
                >
                  <option value="">Sélectionner une classe</option>
                  {classes.map((classe) => (
                    <option key={classe.id} value={classe.id}>
                      {classe.libelle}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <BookOpen className="h-6 w-6 text-[#202654]" />
                  </div>
                  <label htmlFor="semestre" className="block text-sm font-medium text-gray-700">
                    Semestre
                  </label>
                </div>
                <select
                  id="semestre"
                  value={selectedSemestre}
                  onChange={(e) => setSelectedSemestre(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                  required
                >
                  <option value="">Sélectionner un semestre</option>
                  {semestres.map((semestre) => (
                    <option key={semestre.id} value={semestre.id}>
                      {semestre.libelle}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Planning Hebdomadaire</h2>
                <div className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-[#202654]" />
                  <span className="text-sm text-gray-600">
                    {selectedClasse ? `${classes.find(c => c.id === parseInt(selectedClasse))?.libelle || ''}` : 'Sélectionnez une classe'}
                  </span>
                </div>
              </div>
              
              {JOURS_SEMAINE.map((jour) => (
                <div key={jour.id} className={`rounded-lg border ${jour.color} overflow-hidden shadow-sm hover:shadow-md transition-shadow`}>
                  <button
                    type="button"
                    onClick={() => setExpandedDay(expandedDay === jour.id ? null : jour.id)}
                    className="w-full px-6 py-4 flex justify-between items-center hover:bg-opacity-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{jour.icon}</span>
                      <h3 className="text-lg font-medium text-gray-900">{jour.libelle}</h3>
                    </div>
                    {expandedDay === jour.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>

                  {expandedDay === jour.id && (
                    <div className="p-6 bg-white">
                      <div className="space-y-4">
                        {getCoursForDay(jour.id).map((cours, index) => (
                          <div key={cours.id} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex items-center space-x-2">
                                <div className="p-2 bg-[#202654]/10 rounded-lg">
                                  <Clock className="h-5 w-5 text-[#202654]" />
                                </div>
                                <h4 className="text-sm font-medium text-gray-900">Cours {index + 1}</h4>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveCours(cours.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded-full"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Heure début</label>
                                <div className="relative rounded-md shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Clock className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <input
                                    type="time"
                                    value={cours.heure_debut}
                                    onChange={(e) => handleCoursChange(cours.id, 'heure_debut', e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                    required
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Heure fin</label>
                                <div className="relative rounded-md shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Clock className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <input
                                    type="time"
                                    value={cours.heure_fin}
                                    onChange={(e) => handleCoursChange(cours.id, 'heure_fin', e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                    required
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Module</label>
                                <select
                                  value={cours.module}
                                  onChange={(e) => handleCoursChange(cours.id, 'module', e.target.value)}
                                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                  required
                                  disabled={loadingModules}
                                >
                                  <option value="">Sélectionner un module</option>
                                  {modules.map((module) => (
                                    <option key={module.id} value={module.id}>
                                      {module.libelle}
                                    </option>
                                  ))}
                                </select>
                                {loadingModules && (
                                  <p className="mt-1 text-sm text-gray-500">Chargement des modules...</p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Salle</label>
                                <div className="relative rounded-md shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <select
                                    value={cours.salle}
                                    onChange={(e) => handleCoursChange(cours.id, 'salle', e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                    required
                                  >
                                    <option value="">Sélectionner une salle</option>
                                    {salles.map((salle) => (
                                      <option key={salle.id} value={salle.id}>
                                        {salle.libelle}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => handleAddCours(jour.id)}
                          className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter un cours pour {jour.libelle}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/liste-emplois')}
                className="inline-flex justify-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex justify-center px-6 py-3 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
              >
                {saving ? 'Modification en cours...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditEmplois;
